import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";

function formatErr(detail) {
  if (!detail) return "No pudimos iniciar sesión. Reintenta.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/cuenta";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Bienvenido de vuelta");
      navigate(from);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO title="Iniciar sesión" description="Accede a tu cuenta Ley de Datos Chile para gestionar tus documentos y el Módulo Pro." path="/login" noindex />
      <section className="min-h-[calc(100vh-64px)] grid grid-cols-1 lg:grid-cols-2">
        {/* Left: form */}
        <div className="px-6 py-16 sm:py-20 lg:py-24 flex items-center">
          <div className="w-full max-w-md mx-auto">
            <p className="font-mono text-[10px] tracking-[0.22em] text-blue-700 uppercase mb-6">
              Iniciar sesión
            </p>
            <h1 className="font-display text-4xl sm:text-5xl text-slate-900 leading-tight tracking-tight">
              Bienvenido <em className="italic text-blue-700">de vuelta</em>.
            </h1>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Accede para gestionar tus documentos, deep scans y el Módulo Pro.
            </p>

            <form onSubmit={submit} className="mt-10 space-y-5">
              <div>
                <label htmlFor="login-email" className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">
                  Email
                </label>
                <input
                  id="login-email"
                  data-testid="login-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-12 px-4 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all"
                  placeholder="tu@empresa.cl"
                />
              </div>
              <div>
                <label htmlFor="login-pw" className="block font-mono text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2">
                  Contraseña
                </label>
                <input
                  id="login-pw"
                  data-testid="login-password"
                  type="password"
                  required
                  minLength={8}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full h-12 px-4 bg-white border border-slate-300 rounded-xl focus:border-blue-600 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all"
                />
              </div>
              <button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="w-full h-12 rounded-full bg-slate-900 hover:bg-slate-800 active:scale-[0.98] disabled:opacity-60 text-white font-semibold transition-all inline-flex items-center justify-center gap-2"
              >
                {loading ? "Ingresando…" : (<>Iniciar sesión <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>

            <p className="mt-8 text-sm text-slate-600">
              ¿No tienes cuenta?{" "}
              <Link to="/registro" className="font-semibold text-blue-700 hover:text-blue-900">
                Regístrate gratis
              </Link>
            </p>
          </div>
        </div>

        {/* Right: dark brand panel */}
        <div className="relative hidden lg:flex bg-ink-950 text-white overflow-hidden">
          <div className="absolute inset-0 bg-grid-pattern opacity-30 pointer-events-none" />
          <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-blue-600/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col justify-center px-12 max-w-xl">
            <div className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] text-blue-300 uppercase mb-8">
              <ShieldCheck className="w-3.5 h-3.5" />
              Ley 21.719 · Chile
            </div>
            <h2 className="font-display text-4xl xl:text-5xl text-white leading-tight">
              Cumplimiento <em className="italic text-blue-300">visible</em>, no decorativo.
            </h2>
            <p className="mt-6 text-slate-300 leading-relaxed text-sm max-w-md">
              Cada escaneo deja huella en tu cuenta. Cada documento cita su artículo y literal. Cada cambio queda auditado.
            </p>

            <ul className="mt-10 space-y-4">
              {[
                "Escáner gratuito + Deep Scan multi-página",
                "7 documentos legales Art. 14 ter + RAT + CCM Min. Economía",
                "Historial completo · re-scans ilimitados",
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-12 pt-8 border-t border-white/10 font-mono text-[10px] text-slate-500 uppercase tracking-widest">
              v4.0 · session encrypted · TLS 1.3
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
